// ./src/components/features/CreateEventWizard/steps/TimelineStep.tsx
//
// "What happens when?" — the run of show, shown when the Timeline feature is on (ROADMAP A2b #62 P6(e)). Items are made
// in the Timeline page's own `TimelineItemEditDrawer` under a provider scoped by the event; a one-tap starter only opens
// it with a name filled in (`wizardStarters.ts`).
//
// ⚠ The drawer edits times in the EVENT's timezone and suggests each item's phase from the event's own start and end,
//   so the step reads the event first — and the list waits for that read: a drawer opened without it would edit in the
//   browser's zone and call a during-the-party item "before".
// ⓘ A new item starts where the last one ended (the page's own `suggestNextStart`); the FIRST one at the event's start.
// ⓘ Not the Schedule step: sessions there are what GUESTS see and pick; the timeline is the host's and crew's plan.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Stack, Text } from '@mantine/core';
import { OpResult } from '@sdflc/api-helpers';
import { useTranslation } from 'react-i18next';

import { TimelineItemsProvider, useTimelineItemsContext } from '@/contexts';
import { useEvent, useToast } from '@/hooks';
import { TimelineItem, WHOLE_SET } from '@/utils/api';
import { ConfirmModal } from '@/components/generic';
import { TimelineItemEditDrawer, formatTimelineRange, suggestNextStart } from '@/components/features/Timeline';
import { refusalReason } from '@/utils';

import { useEventWizard } from '../WizardContext';
import { WizardItemRow } from '../components/WizardItemRow';
import { WizardListSection } from '../components/WizardListSection';
import { TIMELINE_STARTERS, startersFor, unusedStarters } from '../wizardStarters';
import { useTypeIdeas } from '../useTypeIdeas';

/** Earliest first; an item with no time after every timed one. */
const byTimeNullsLast = (a?: string | null, b?: string | null): number => {
  if (a && b) {
    return a.localeCompare(b);
  }

  return a ? -1 : b ? 1 : 0;
};

const TimelineStepBody: React.FC<{ eventId: string }> = ({ eventId }) => {
  const { t } = useTranslation(['events', 'common']);
  const toast = useToast();
  const { saving } = useEventWizard();
  const { timelineItems, list, isLoaded, remove } = useTimelineItemsContext();
  const { event, error: eventError, reload: reloadEvent } = useEvent(eventId);

  const [readError, setReadError] = useState<OpResult | null>(null);
  const [drawer, setDrawer] = useState<{ recordId: string | null; name?: string } | null>(null);
  const [pendingRemove, setPendingRemove] = useState<TimelineItem | null>(null);
  const [removing, setRemoving] = useState(false);

  const load = useCallback(async () => {
    const res = await list({ eventId: [eventId] }, { page: 1, pageSize: WHOLE_SET }, undefined, true);

    setReadError(res.didSucceed() ? null : res);
  }, [list, eventId]);

  useEffect(() => {
    void load();
  }, [load]);

  const sorted = useMemo(
    () => [...timelineItems].sort((a, b) => byTimeNullsLast(a.startsAt, b.startsAt)),
    [timelineItems],
  );
  const defaultStartsAt = useMemo(() => suggestNextStart(timelineItems) ?? event?.startsAt ?? null, [timelineItems, event?.startsAt]);
  // The label is both the button and the saved name, so it is translated before anything compares it.
  // ⓘ The TYPE's own ideas when it has any (#62 P6(h)), else the generic ones.
  const typeIdeas = useTypeIdeas('timeline');
  const starters = unusedStarters(
    startersFor(typeIdeas, TIMELINE_STARTERS.map((s) => ({ ...s, label: t(`events:wizard.timeline.starter.${s.key}`, { defaultValue: s.label }) })), 'Timeline'),
    timelineItems.map((i) => i.name),
  );

  const afterSave = () => {
    setDrawer(null);
    void load();
  };

  const confirmRemove = async () => {
    if (!pendingRemove?.id) {
      return;
    }

    setRemoving(true);

    const res = await remove(pendingRemove.id);

    setRemoving(false);

    if (!res.didSucceed()) {
      toast.error(refusalReason(res));

      return;
    }

    setPendingRemove(null);
    void load();
  };

  return (
    <Stack gap="lg">
      <Text size="md" c="dimmed">
        {t('events:wizard.timeline.intro', {
          defaultValue:
            'Plan the run of show for you and your crew — set-up, doors, speeches, clean-up — with times and who is in charge. Guests do not see it.',
        })}
      </Text>

      <WizardListSection
        title={t('events:wizard.timeline.title', { defaultValue: 'Timeline' })}
        addLabel={t('events:wizard.timeline.add', { defaultValue: 'Add an item' })}
        onAdd={() => setDrawer({ recordId: null })}
        gate={null}
        gateWhat=""
        disabled={saving || !event}
        starters={starters.map((s) => ({ key: s.key, label: s.label, icon: s.icon, onClick: () => setDrawer({ recordId: null, name: s.label }) }))}
        isLoaded={(isLoaded || !!readError) && (!!event || !!eventError)}
        readError={readError ?? eventError}
        onRetry={() => {
          void reloadEvent();
          void load();
        }}
        errorTitle={t('events:wizard.timeline.loadFailed', { defaultValue: 'Could not load the timeline' })}
        operation="timelineItemList"
        emptyText={t('events:wizard.timeline.none', { defaultValue: 'No items yet.' })}
        isEmpty={sorted.length === 0}
      >
        {sorted.map((item) => (
          <WizardItemRow
            key={item.id}
            icon="Timeline"
            title={item.name ?? ''}
            subtitle={formatTimelineRange(item.startsAt, item.endsAt, event?.timezone) ?? null}
            disabled={saving}
            onEdit={() => setDrawer({ recordId: item.id as string })}
            onRemove={() => setPendingRemove(item)}
          />
        ))}
      </WizardListSection>

      <TimelineItemEditDrawer
        opened={!!drawer}
        onClose={() => setDrawer(null)}
        recordId={drawer?.recordId ?? null}
        eventId={eventId}
        eventTimezone={event?.timezone ?? null}
        eventStartsAt={event?.startsAt ?? null}
        eventEndsAt={event?.endsAt ?? null}
        defaultStartsAt={defaultStartsAt}
        defaultName={drawer?.name ?? null}
        onCreated={afterSave}
        onUpdated={afterSave}
      />

      <ConfirmModal
        opened={!!pendingRemove}
        onClose={() => setPendingRemove(null)}
        onConfirm={() => void confirmRemove()}
        type="danger"
        confirmInProgress={removing}
        title={t('events:wizard.timeline.removeTitle', { defaultValue: 'Remove this item?' })}
        message={t('events:wizard.tickets.removeMessage', { defaultValue: '“{{name}}” will be removed from this event.', name: pendingRemove?.name ?? '' })}
        confirmLabel={t('common:actions.remove', { defaultValue: 'Remove' })}
      />
    </Stack>
  );
};

export const TimelineStep: React.FC = () => {
  const { t } = useTranslation(['events']);
  const { createdEventId } = useEventWizard();

  if (!createdEventId) {
    return (
      <Text size="md" c="dimmed">
        {t('events:wizard.tickets.noEvent', { defaultValue: 'Your event is created at the end of the Guests step.' })}
      </Text>
    );
  }

  return (
    <TimelineItemsProvider initialFilters={{ eventId: [createdEventId] }} pageSize={WHOLE_SET} autoLoad={false}>
      <TimelineStepBody eventId={createdEventId} />
    </TimelineItemsProvider>
  );
};
